import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { useNavigate } from 'react-router-dom';
import { useUrlString } from '../../hooks/useUrlState';
import { useFocusRow } from '../../hooks/useFocusRow';
import { Check, X, PauseCircle, CornerUpLeft, Eye, Search, Undo2, ShieldAlert, ExternalLink, Clock, Lock, Banknote, HandCoins, Truck, PackageCheck, Hand, TriangleAlert, Info, ArrowRight, CalendarDays, UserRound, Store, ShoppingCart, Hash, IndianRupee, Wallet, Package, Boxes, Footprints, Headset, Warehouse, Clock3, Paperclip } from 'lucide-react';
import { request } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { useUrlTab } from '../../hooks/useUrlTab';
import { useServerSort } from '../../hooks/useServerSort';
import { formatIst, formatIstDate, formatIstRelative } from '../../lib/time';
import { inr, inr2, plural } from '../../lib/format';
import { useAuthStore } from '../../store/auth';
import {
  PageHeader, Card, Button, FilterResetButton, Input, Select, Tooltip, Tabs, Textarea, Modal, Alert, EmptyState, Skeleton, SectionHead, message, cn,
} from '../../ui';
import { RoleBadge } from '../../components/RoleBadge';
import { TwDataTable, Pill } from '../../components/TwDataTable';
import { ageBucket, type AgeBucket } from '../../lib/waiting';
import { CodeChip } from '../../ui/CodeChip';
import { entityHue } from '../../ui/EntityAvatar';
import { RetailerLink, OrderLink, UserLink, FarmerLink } from '../../components/EntityLinks';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { DecisionModal, balanceText, type LaneAction } from './DecisionModal';
import { renderFacetOption } from '../../lib/facetOptions';

interface Lane {
  key: string;
  label: string;
  group: string;
  waitingFor: string;
  blocks: string;
  link: string | null;
  actions: LaneAction[];
  revert: { from: Record<string, string>; effect: string } | null;
}
interface Detail {
  label: string;
  value: string | number | null;
  kind?: 'money' | 'qty' | 'doc' | 'date' | 'warn' | 'balance' | 'wallet' | 'attachments';
  link?: { kind: 'retailer' | 'user' | 'order'; id: string; role?: string | null };
  /** The evidence behind the decision, rendered inline as thumbnails rather than counted. */
  files?: string[];
  /** A person's role, drawn as the app's coloured pill beside their name (never as text). */
  role?: string | null;
  /**
   * A short identifying CODE for the value — a warehouse code, a SKU — drawn as a monospace chip in
   * front of the name rather than glued to it with a separator.
   *
   * `WH-BANGLORE · Banglore` in one grey string made the reader do the parsing: the half that
   * identifies the place and the half that names it look identical, so neither reads first.
   */
  chip?: string | null;
  /** A list where one value would have to be a truncated sentence — e.g. the dealers a deposit covers. */
  breakdown?: Array<{ name: string; amount: number; sub?: string | null }>;
  /**
   * What the breakdown's footer counts up, e.g. "Total units". Absent = "Total allocated", the
   * money wording the list was first built for. A `kind` of 'qty' also switches the figures from
   * rupees to plain counts — a consignment of 21 bottles is not ₹21.00.
   */
  totalLabel?: string;
}
interface Item {
  domain: string; id: string; docNo: string | null; title: string; subtitle: string | null;
  amount: number | null; status: string;
  raisedByName: string | null; raisedById: string | null; raisedByRole: string | null;
  raisedAt: string | null; ageDays: number; details: Detail[]; meta: Record<string, any>;
}
/**
 * Is this decision worth OFFERING on this row?
 *
 * Three rules, all declared by the registry rather than known here (see approval-registry.ts):
 *   • `whenStatus`         — the record must be in a state the endpoint will accept the act from.
 *     One lane holds rows in several states at once (complaints: escalated, overdue, resolved), and
 *     without this the resolved ones were still drawn with a "Resolve" button that could only ever
 *     come back "already resolved".
 *   • `hideWhenSelfRaised` — don't route work back to the person already holding it.
 *   • `hideWhenSelfDecided`— don't offer the second half of a two-person decision to whoever made
 *     the first half; `unlessRole` carries the domain's own exemption.
 *
 * A hidden button is never a permission decision — the endpoint still checks everything it did
 * before. This only stops the app asking for a remark and a confirmation for a refusal.
 */
function actionApplies(a: LaneAction, r: Item, meId?: string, myRoles: string[] = []): boolean {
  if (a.whenStatus?.length && !a.whenStatus.includes(String(r.status ?? ''))) return false;
  const isMe = (v: unknown) => !!meId && !!v && String(meId) === String(v);
  if (a.hideWhenSelfRaised && isMe(r.raisedById)) return false;
  if (a.hideWhenSelfDecided && isMe(r.meta?.decidedById)) {
    const exempt = a.hideWhenSelfDecided.unlessRole;
    if (!exempt || !myRoles.includes(exempt)) return false;
  }
  /*
   * An action the ROW says does not apply.
   *
   * "Fund this from the collection money they hold" is not a choice against somebody holding none —
   * the endpoint would refuse it, and a button whose only outcome is a 400 teaches the user to
   * distrust the buttons that work. Mirrors a field's `showWhen`; an unanswered watched value hides
   * the action, because an option nobody has established is not one to offer.
   */
  if (a.showWhen) {
    const raw = r.meta?.[a.showWhen.meta];
    const v = raw === undefined || raw === null ? '' : String(raw);
    if (!v) return false;
    if (a.showWhen.equals && !a.showWhen.equals.includes(v)) return false;
    if (a.showWhen.notEquals && a.showWhen.notEquals.includes(v)) return false;
  }
  return true;
}

interface MyWork {
  domain: string; id: string; docNo: string | null; title: string; subtitle: string | null;
  amount: number | null; outcome: 'RETURNED' | 'REJECTED'; status: string; reason: string | null;
  decidedByName: string | null; decidedByRole: string | null; decidedAt: string | null;
  ageDays: number; link: string | null; nextStep: string;
}
interface Decided {
  domain: string; laneLabel: string; id: string; docNo: string | null; status: string;
  amount: number | null; decidedAt: string | null; decidedBy: string | null; decidedByRole: string | null;
  /** The decider's user id — a name cannot answer "was this mine?". */
  decidedById: string | null;
  party: string | null; revertTo: string | null;
  /**
   * WHO the decision was about, as a record rather than a label: what kind of thing the party is,
   * its id, the role to draw beside a person's name, and the one fact that qualifies them here (the
   * day an attendance fix covers). Null on the lanes whose subject is a place or a product.
   */
  partyKind?: 'retailer' | 'user' | 'farmer' | null;
  partyId?: string | null;
  partyRole?: string | null;
  partySub?: string | null;
  /** The evidence the decision was made on (payment proof today) — see the decided endpoint. */
  files?: string[];
}

const GROUP_ORDER = ['SALES', 'MONEY', 'STOCK', 'FIELD', 'SERVICE'];
const GROUP_LABEL: Record<string, string> = {
  SALES: 'Sales', MONEY: 'Money', STOCK: 'Stock', FIELD: 'Field force', SERVICE: 'Service',
};

/**
 * One colour per kind of work, used on BOTH the tile and the table's type chip — so a row in the
 * list is recognisable as "money" or "stock" without reading it. Written as whole literal class
 * strings because Tailwind only emits classes it can see in the source; a built-up
 * `bg-${x}-500/12` would compile to nothing.
 */
const GROUP_STYLE: Record<string, { bar: string; text: string; chip: string; tile: string; wash: string; washOn: string }> = {
  SALES: {
    bar: 'bg-sky-500', text: 'text-sky-600 dark:text-sky-400',
    chip: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300',
    tile: 'hover:border-sky-500/60',
    wash: 'from-sky-500/[0.10]', washOn: 'border-sky-500/60 bg-sky-500/12 from-sky-500/[0.16]',
  },
  MONEY: {
    bar: 'bg-brand-500', text: 'text-brand-700 dark:text-brand-400',
    chip: 'border-brand-500/30 bg-brand-500/10 text-brand-700 dark:text-brand-300',
    tile: 'hover:border-brand-500/60',
    wash: 'from-brand-500/[0.10]', washOn: 'border-brand-500/60 bg-brand-500/12 from-brand-500/[0.16]',
  },
  STOCK: {
    bar: 'bg-violet-500', text: 'text-violet-600 dark:text-violet-400',
    chip: 'border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300',
    tile: 'hover:border-violet-500/60',
    wash: 'from-violet-500/[0.10]', washOn: 'border-violet-500/60 bg-violet-500/12 from-violet-500/[0.16]',
  },
  FIELD: {
    bar: 'bg-harvest-500', text: 'text-harvest-700 dark:text-harvest-300',
    chip: 'border-harvest-500/35 bg-harvest-400/15 text-harvest-700 dark:text-harvest-300',
    tile: 'hover:border-harvest-500/60',
    wash: 'from-harvest-400/[0.15]', washOn: 'border-harvest-500/60 bg-harvest-400/15 from-harvest-400/[0.22]',
  },
  SERVICE: {
    bar: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400',
    chip: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
    tile: 'hover:border-rose-500/60',
    wash: 'from-rose-500/[0.10]', washOn: 'border-rose-500/60 bg-rose-500/12 from-rose-500/[0.16]',
  },
};
const groupStyle = (g?: string) => GROUP_STYLE[g ?? ''] ?? GROUP_STYLE.SALES;
/**
 * The kind of work as a glyph, for the one place a single record is shown on its own (the summary
 * dialog's header). It varies with the lane's GROUP, like the colour it sits on — never one fixed mark
 * repeated down a list.
 */
const GROUP_ICON: Record<string, typeof Truck> = {
  SALES: ShoppingCart, MONEY: Wallet, STOCK: Boxes, FIELD: Footprints, SERVICE: Headset,
};

/**
 * Lane names/colours for the "Back with you" tab.
 *
 * That tab is shown to people who hold NO approval permission, so `/approvals/lanes` returns them an
 * empty list and the usual lookup has nothing in it. These few labels keep their own returned work
 * readable without pretending they can approve anything.
 */
const LANE_LABEL_FALLBACK: Record<string, string> = {
  order: 'Order', payment: 'Payment', expense_report: 'Expense claim',
  advance_request: 'Field advance', return: 'Sales return', attendance_regularization: 'Attendance fix',
};
const LANE_GROUP_FALLBACK: Record<string, string> = {
  order: 'SALES', payment: 'MONEY', expense_report: 'FIELD',
  advance_request: 'FIELD', return: 'MONEY', attendance_regularization: 'FIELD',
};

/**
 * The lane name in a table cell, tinted by its kind of work.
 *
 * Deliberately NOT `Pill`: that is `whitespace-nowrap`, which is right for a status word but wrong
 * here — the longest lane name ("Field advance — disbursement") overflowed its fixed-width column
 * and printed on top of the document number. This wraps to a second line instead.
 */
function TypeChip({ group, children }: { group?: string; children: React.ReactNode }) {
  return (
    <span className={`inline-block max-w-full rounded-card border px-2 py-0.5 type-label leading-4 ${groupStyle(group).chip}`}>
      {children}
    </span>
  );
}

/** Age → urgency. A decision nobody has taken for a week is the whole reason this page exists. */
/* The app's one ageing scale, in Pill's vocabulary. The thresholds moved to lib/waiting when the
   transfers register needed the same buckets — see AGE_TAG there for the same scale in Tag's. */
const AGE_PILL: Record<AgeBucket, string> = { stale: 'red', ageing: 'orange', day: 'gold', fresh: 'default' };
const ageTone = (d: number) => AGE_PILL[ageBucket(d)];

/**
 * WHO — a face, a name and their role as a coloured pill.
 *
 * All three tables here printed the role as a grey lowercase caption under the name ("warehouse
 * manager", "super admin"). That is exactly the shape RoleBadge exists to replace: an approvals queue
 * is a MIXED list of people, and telling an Accounts decision from a Sales Head's meant reading two
 * lines of identical grey on every row. One hue per role settles it at a glance, and the identity
 * mark gives the eye something to come back to after scrolling.
 */
/*
 * Name + role, with NO initials disc.
 *
 * An avatar earns its place where it identifies somebody you would otherwise have to read to
 * recognise — a dealer in a long register, a photo on a profile. Here it was a coloured circle
 * holding the first two letters of the name printed immediately beside it, on every row of a
 * queue whose real content is the DOCUMENT and the DECISION. It repeated a word rather than
 * adding one, and pushed the name it duplicated into a narrower column.
 */
function Person({ name, role, sub }: { name?: string | null; role?: string | null; sub?: React.ReactNode }) {
  if (!name) return <span className="text-subtle">—</span>;
  return (
    <div className="min-w-0">
      {/* The pill sits BESIDE the name — name → role, read as one phrase — and only wraps under it
          when the column is too narrow for both. */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <span className="min-w-0 wrap-break-word text-content" title={name}>{name}</span>
        <RoleBadge role={role} />
      </div>
      {sub ? <div className="mt-0.5 truncate type-caption text-subtle">{sub}</div> : null}
    </div>
  );
}

/**
 * WHO / WHAT — the party, as a way to reach them.
 *
 * Every other register in the app lets you click the dealer and land on the dealer (RetailerLink and
 * friends, gated on the viewer's own permissions so they never offer a 403). The one screen that
 * reviews decisions ACROSS the lanes printed the name as dead text, which is exactly where the
 * question "who is this?" is most likely to be asked — a Super Admin judging a decision they had no
 * part in. A person also carries their role as a coloured pill here, the same mark they wear in
 * Decided by on the same row, so "PAWAR BROTHERS" (a dealer) and "Asmita Kale · Sales Person" can no
 * longer read as the same kind of thing.
 *
 * `sub` is the fact that qualifies the party in this lane and is NOT part of their name — the day an
 * attendance fix covers. It used to be concatenated into the name with a dot.
 */
function PartyCell({ name, kind, id, role, sub, className }: {
  name?: string | null; kind?: string | null; id?: string | null;
  role?: string | null; sub?: React.ReactNode; className?: string;
}) {
  if (!name) return <span className="text-subtle">—</span>;
  const label = <span className="wrap-break-word" title={name}>{name}</span>;
  const linked = kind === 'retailer' ? <RetailerLink id={id}>{label}</RetailerLink>
    : kind === 'farmer' ? <FarmerLink id={id}>{label}</FarmerLink>
      : kind === 'user' ? <UserLink id={id} role={role}>{label}</UserLink>
        : label;
  return (
    <div className={cn('min-w-0', className)}>
      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
        {linked}
        {kind === 'user' ? <RoleBadge role={role} /> : null}
      </div>
      {sub ? <div className="mt-0.5 type-caption text-subtle">{sub}</div> : null}
    </div>
  );
}

/**
 * One lane tile. Fixed height and a grid cell of equal width, so however many lanes a role holds the
 * rows always line up — the first cut let each tile size to its own label and the result was a ragged
 * block with the group headings floating at different heights.
 */
function LaneTile({
  group, label, count, oldestDays, active, muted, onClick, title,
}: {
  group?: string; label: string; count: number; oldestDays: number;
  active: boolean; muted: boolean; onClick: () => void; title?: string;
}) {
  const g = groupStyle(group);
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      // Every lane now carries its GROUP's colour as a wash, not just as a 4px rail — with 17 tiles
      // in the band, a wall of white rectangles has to be read one label at a time, where a washed
      // band lets the reader jump straight to "the money ones". Selection reuses the lane's OWN hue
      // (stronger, solid, thicker rail, one step up the elevation ladder) rather than a single
      // brand-green highlight: a green ring on a violet stock tile put two unrelated colours on one
      // control, and the eye read "green thing" before "selected thing". Same rule as ui/StatCard.
      //
      // `bg-surface-1` (the old resting fill) is not a token the theme defines — it compiled to
      // nothing, so these tiles were transparent rather than white. `bg-surface` is the real name.
      className={`relative flex h-[92px] w-full flex-col justify-between overflow-hidden rounded-card border bg-linear-to-b to-70% to-transparent px-3 py-2 text-left transition
        ${active ? `shadow-e2 ${g.washOn}` : `border-line bg-surface shadow-e1 ${g.wash} ${g.tile}`}
        ${muted ? 'opacity-55' : ''}`}
    >
      <span className={`absolute inset-y-0 left-0 ${active ? 'w-1.5' : 'w-1'} ${g.bar} ${muted && !active ? 'opacity-40' : ''}`} aria-hidden />
      <span className={`type-overline ${g.text}`}>{GROUP_LABEL[group ?? ''] ?? ''}</span>
      <span className="line-clamp-2 type-label leading-4 text-content">{label}</span>
      <span className="flex items-baseline gap-2">
        <span className="type-metric leading-none text-content">{count}</span>
        {oldestDays > 0 ? <Pill color={ageTone(oldestDays)}>{oldestDays}d</Pill> : null}
      </span>
    </button>
  );
}
/**
 * One icon per action key.
 *
 * "Send back" is a RETURN arrow, not a pencil: the approver is not editing anything, they are handing
 * the record back to whoever raised it. A pencil said "edit this" — the one thing that button does
 * not do. (`Undo2` is reserved for the Super Admin's reversal, which is a different, heavier act.)
 */
const ICON: Record<string, JSX.Element> = {
  approve: <Check size={16} />, reject: <X size={16} />, hold: <PauseCircle size={16} />,
  correction: <CornerUpLeft size={16} />, send_back: <CornerUpLeft size={16} />, resolve: <Check size={16} />,
  /*
   * Paying the advance out is a BANKNOTE — the glyph the same act already carries on the advance
   * requests screen, and the one this file gives “money leaves the company” on the claim payout below.
   * It was a tick, which said no more than “yes” about the one button in the lane that moves money.
   *
   * That tick also collided. `fund_from_cash` had no glyph of its own, so it fell through to the
   * default tick and the advance payout lane drew TWO identical ticks side by side — for opposite
   * answers to “do they need paying?”. One sends money out of the company; the other pays nothing and
   * closes the request against collected cash the rep is already holding. Coins in a hand say money
   * ALREADY IN HAND, which is exactly what that button spends.
   */
  disburse: <Banknote size={16} />, fund_from_cash: <HandCoins size={16} />,
  /*
   * `close` is a PADLOCK, not a second tick. The complaint lane now offers Resolve and Close side by
   * side, and they are different decisions — one states what was done, the other certifies the matter
   * is over and cannot be added to. Two identical ticks in adjacent buttons is a mis-click waiting to
   * happen on the one action that is hardest to undo.
   */
  close: <Lock size={16} />, cancel: <X size={16} />, open: <ExternalLink size={16} />,
  /*
   * Sending stock OUT and taking it IN are different acts, and both used to carry the generic
   * open-elsewhere arrow — the same glyph the plain "open the record" action uses, so one icon meant
   * three things. Both still open the transfers screen (batch picking and line-by-line receipt cannot
   * be done from a row), but the icon now says WHICH way the stock is moving.
   */
  dispatch: <Truck size={16} />, receive: <PackageCheck size={16} />,
  // Taking work on is not a decision about it — a hand, not a tick, so it cannot be mistaken for
  // "this is dealt with".
  take_up: <Hand size={16} />,
};

/**
 * Where a key means DIFFERENT things in different lanes, the lane decides the glyph.
 *
 * An action key is only unique WITHIN its lane, so the key alone cannot always name the act. `close`
 * is the case in point: on a complaint it seals the matter (the padlock above), but on a claim payout
 * `close` is labelled "Mark paid" — the moment money actually leaves the company to reimburse a rep.
 * A padlock there read as "file this away", the opposite of what pressing it does, on the one button
 * in the lane whose whole purpose is to pay somebody.
 *
 * Money out is a BANKNOTE — the same glyph Disburse already carries on the advance requests screen,
 * so the act looks the same wherever the reader meets it.
 */
const LANE_ICON: Record<string, JSX.Element> = {
  'expense_settlement:close': <Banknote size={16} />,
  // Money coming BACK from a rep — a hand giving coins, not the stock-receipt parcel `receive` means elsewhere.
  'expense_recovery:receive': <HandCoins size={16} />,
};

/** The icon for an action, letting its lane override a key that is shared but not synonymous. */
const actionIcon = (domain: string, key: string): JSX.Element =>
  LANE_ICON[`${domain}:${key}`] ?? ICON[key] ?? <Check size={16} />;

/** Is this string an ISO instant (so it deserves a time as well as a date)? */
const isInstant = (v: unknown) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v);

/**
 * The row's full detail, as the lane itself described it.
 *
 * Rendered generically from `details` rather than per-lane markup: the fields that matter to an order
 * are not the fields that matter to a stock adjustment, and the lane is the only thing that knows.
 * `kind` carries just enough for the page to format — money right-aligned, a date given its time, a
 * `warn` tinted so "no bill attached" or "past the credit limit" is not read as ordinary text.
 */
/** Wrap a value in the shared entity link when the lane gave us one to link to. */
/**
 * A ROUTE, WHERE THE ROW IS ABOUT A MOVEMENT.
 *
 * Every other lane's title is a name; a stock transfer's is a JOURNEY, and "Pune Warehouse →
 * Banglore" as one grey string made a queue of movements a column of similar-looking prose — the
 * codes people actually say ("send it from PUNE") were not in it at all, and two Bengaluru
 * warehouses were told apart by reading to the end. Drawn as the same identity-coloured code chips
 * the transfers register uses, so a warehouse is the same colour wherever it appears.
 *
 * Falls back to the plain title for every lane that is not a movement, which is most of them.
 */
function WhatTitle({ title, meta }: { title: string; meta?: Record<string, any> }) {
  const from = meta?.fromCode;
  const to = meta?.toCode;
  if (!from || !to) return <div className="truncate text-content">{title}</div>;
  return (
    /*
     * THE CODES ON THEIR OWN LINE, the site names under them.
     *
     * Both on one line does not fit a 340px column: with the names inline the chips were squeezed to
     * "WH-PU…" and "WH-BANGL…", which destroys the one part that identifies the warehouse. Given the
     * line to themselves the two chips always render whole — and the names, which are the half that
     * can afford to ellipsise, sit underneath where there is room for them.
     *
     * `shrink-0` on the chips as well, so nothing can take it back.
     */
    <div className="min-w-0">
      <div className="flex min-w-0 flex-nowrap items-center gap-1.5">
        <CodeChip hue={entityHue(from)} className="shrink-0">{from}</CodeChip>
        <ArrowRight size={13} aria-hidden className="shrink-0 text-subtle" />
        <CodeChip hue={entityHue(to)} className="shrink-0">{to}</CodeChip>
      </div>
      <div className="truncate type-caption text-content" title={`${meta?.fromWarehouse ?? from} → ${meta?.toWarehouse ?? to}`}>
        {meta?.fromWarehouse ?? from} → {meta?.toWarehouse ?? to}
      </div>
    </div>
  );
}

function DetailValue({ d, children }: { d: Detail; children: React.ReactNode }) {
  if (!d.link) return <>{children}</>;
  if (d.link.kind === 'retailer') return <RetailerLink id={d.link.id}>{children}</RetailerLink>;
  if (d.link.kind === 'order') return <OrderLink id={d.link.id}>{children}</OrderLink>;
  return <UserLink id={d.link.id} role={d.link.role}>{children}</UserLink>;
}

/**
 * How a single fact is laid out.
 *
 * A decision modal carries two very different kinds of fact and they were being drawn the same way:
 * short identifying ones ("WH-PUNE", "9 units", "05:33 PM") that pair naturally with a label, and
 * whole sentences ("In transit — it has left the source and is not sellable anywhere yet") that do
 * not. Right-aligning a sentence inside a half-width column broke it into four ragged lines and
 * pushed the label away from the thing it labels.
 *
 * So the grid sorts them: pairs go in the columns, sentences get the full width and their own band.
 */
/*
 * Deliberately WELL past the length of a name.
 *
 * The first cut used 40, which is shorter than plenty of real dealer names — so
 * "Shree Krishna Agro Agencies, Nashik" was reclassified as prose and moved out of the pairs grid,
 * taking its `dt`/`dd` (and the link inside it) with it. A value is only prose when it is longer
 * than any identifier could plausibly be; the `warn` kind carries the short explanations.
 */
const LONG_VALUE = 90;

function isSentence(d: Detail, body: string): boolean {
  // A warning is always given room — it is the one line the reader must not skim.
  if (d.kind === 'warn') return true;
  // Never a figure or an identifier, however long: those belong beside their label.
  if (d.kind === 'money' || d.kind === 'doc' || d.kind === 'date'
      || d.kind === 'qty' || d.kind === 'balance' || d.kind === 'wallet') return false;
  // Long AND made of words.
  return body.length > LONG_VALUE && body.trim().includes(' ');
}

/**
 * What KIND of fact a value is, as an icon and a tint — used by the dialog look only.
 *
 * Read from the fact's own shape (who it links to, its kind, whether it carries a code), never from its
 * label: every lane words its facts differently, and only the lane knows its wording. Keyed by meaning
 * like `SECTION_TINT`, and red is absent for the same reason — red means a problem, and a date is not one.
 */
const FACT_TINT = {
  time: 'bg-sky-500/10 text-sky-600 dark:bg-sky-400/10 dark:text-sky-400',
  place: 'bg-teal-500/10 text-teal-600 dark:bg-teal-400/10 dark:text-teal-400',
  person: 'bg-violet-500/10 text-violet-600 dark:bg-violet-400/10 dark:text-violet-400',
  party: 'bg-brand-600/10 text-brand-700 dark:bg-brand-400/10 dark:text-brand-400',
  document: 'bg-indigo-500/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400',
  money: 'bg-amber-500/10 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400',
  quantity: 'bg-lime-600/10 text-lime-700 dark:bg-lime-400/10 dark:text-lime-400',
  other: 'bg-surface-3 text-muted',
} as const;

function factLook(d: Detail): { Icon: typeof Truck; tint: string } {
  if (d.link?.kind === 'user' || d.role) return { Icon: UserRound, tint: FACT_TINT.person };
  if (d.link?.kind === 'retailer') return { Icon: Store, tint: FACT_TINT.party };
  if (d.link?.kind === 'order') return { Icon: ShoppingCart, tint: FACT_TINT.document };
  // Only a warehouse carries a code chip today (a transfer's From and To).
  if (d.chip) return { Icon: Warehouse, tint: FACT_TINT.place };
  switch (d.kind) {
    case 'date': return { Icon: CalendarDays, tint: FACT_TINT.time };
    case 'money': return { Icon: IndianRupee, tint: FACT_TINT.money };
    case 'balance': case 'wallet': return { Icon: Wallet, tint: FACT_TINT.money };
    case 'qty': return { Icon: Package, tint: FACT_TINT.quantity };
    case 'doc': return { Icon: Hash, tint: FACT_TINT.document };
    default: return { Icon: Info, tint: FACT_TINT.other };
  }
}

/**
 * One end of a movement, in its warehouse's identity hue — the same colour its code chip wears on the
 * transfers register and in every picker, so the route reads as places you already recognise.
 */
function RouteEnd({ caption, code, name }: { caption: string; code: string; name?: string | null }) {
  const hue = entityHue(code);
  return (
    <div
      className="flex min-w-0 items-center gap-2.5 rounded-control px-3 py-2"
      style={{
        background: `color-mix(in srgb, ${hue} 12%, var(--gb-surface))`,
        boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 35%, transparent)`,
      }}
    >
      <span
        className="grid size-8 shrink-0 place-items-center rounded-md"
        style={{ background: `color-mix(in srgb, ${hue} 20%, transparent)`, color: hue }}
        aria-hidden
      >
        <Warehouse size={16} />
      </span>
      <div className="min-w-0">
        <div className="type-caption text-subtle">{caption}</div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="type-body-strong text-content">{name ?? code}</span>
          <CodeChip hue={hue}>{code}</CodeChip>
        </div>
      </div>
    </div>
  );
}

function DetailGrid({ details, maxCols = 3, look = 'row' }: {
  details: Detail[];
  /**
   * `row` is the quiet label/value list an expanded table row carries. `dialog` is a record on its own:
   * each fact a tile with an icon for what kind of fact it is, and lists and sentences as sections.
   */
  look?: 'row' | 'dialog';
  /**
   * Cap the column count. The grid's breakpoints are VIEWPORT-based, so on a wide screen it lays out
   * three columns even when its container is a 860px dialog — each column then ~230px, which turned
   * "In transit — it has left the source and is not sellable anywhere yet" into five ragged lines.
   * A prop rather than a class override, because `cn` here has no tailwind-merge: a caller passing
   * `sm:grid-cols-2` would lose to the component's own `xl:grid-cols-3` instead of replacing it.
   */
  maxCols?: 2 | 3;
}) {
  /* A row can carry its content in `breakdown` or `files` rather than in `value` — dropping those for
     an empty value hid the deposit's whole dealer list whenever the summary line happened to be null. */
  const shown = (details ?? []).filter((d) =>
    (d.value !== null && d.value !== undefined && d.value !== '') || d.breakdown?.length || d.files?.length);
  if (!shown.length) return <p className="type-body text-subtle">No further detail recorded.</p>;
  const dialog = look === 'dialog';

  /** The display string for a row, resolved once — the split below turns on its length. */
  const bodyOf = (d: Detail): string => {
    const signed = d.kind === 'balance' || d.kind === 'wallet' ? balanceText(Number(d.value), d.kind) : null;
    if (signed) return signed.text;
    if (d.kind === 'money') return inr2(d.value);
    if (d.kind === 'date') return isInstant(d.value) ? formatIst(String(d.value)) : formatIstDate(String(d.value));
    return String(d.value);
  };

  /*
   * Sentences and lists render BELOW the pairs rather than in place.
   *
   * A full-width row in the middle of a two-column grid leaves a hole beside the fact above it and
   * splits the pairs into two runs the eye has to rejoin. Facts first, then what is said about them,
   * reads in the order someone actually wants it.
   */
  const pairs = shown.filter((d) => !d.breakdown?.length && d.kind !== 'attachments' && !isSentence(d, bodyOf(d)));
  const blocks = shown.filter((d) => d.breakdown?.length || d.kind === 'attachments' || isSentence(d, bodyOf(d)));

  return (
    <div className="flex flex-col gap-3">
      {/* THE DIALOG LOOK: each fact its own tile, label above value, with an icon for its KIND — a
          date, a person, a place, a document — so the shape of the record reads before the words. */}
      {pairs.length > 0 && dialog && (
        <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {pairs.map((d, i) => {
            const signed = d.kind === 'balance' || d.kind === 'wallet' ? balanceText(Number(d.value), d.kind) : null;
            const { Icon, tint } = factLook(d);
            return (
              <div key={`${d.label}-${i}`} className="flex min-w-0 items-start gap-2.5 rounded-control border border-line/60 bg-surface-2/40 px-3 py-2">
                <span className={`mt-0.5 grid size-7 shrink-0 place-items-center rounded-md ${tint}`} aria-hidden>
                  <Icon size={15} />
                </span>
                <div className="min-w-0 flex-1">
                  <dt className="type-caption text-subtle">{d.label}</dt>
                  <dd className={`mt-0.5 min-w-0 break-words type-body-strong ${
                    signed ? `tabular-nums ${signed.valueCls}`
                      : d.kind === 'money' || d.kind === 'qty' ? 'tabular-nums text-content'
                        : d.kind === 'date' ? 'whitespace-nowrap tabular-nums text-content'
                          : d.kind === 'doc' ? 'font-mono text-content' : 'text-content'}`}
                  >
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      {d.chip ? <CodeChip hue={entityHue(d.chip)}>{d.chip}</CodeChip> : null}
                      <DetailValue d={d}>{bodyOf(d)}</DetailValue>
                      {(d.role ?? d.link?.role) ? <RoleBadge role={d.role ?? d.link?.role} /> : null}
                    </span>
                  </dd>
                </div>
              </div>
            );
          })}
        </dl>
      )}

      {pairs.length > 0 && !dialog && (
        <dl className={`grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2 ${maxCols === 3 ? 'xl:grid-cols-3' : ''}`}>
          {pairs.map((d, i) => {
            const signed = d.kind === 'balance' || d.kind === 'wallet' ? balanceText(Number(d.value), d.kind) : null;
            const body = bodyOf(d);
            return (
              <div key={`${d.label}-${i}`} className="flex min-w-0 items-baseline justify-between gap-3 border-b border-line/60 pb-1.5">
                <dt className="shrink-0 type-label text-subtle">{d.label}</dt>
                <dd className={`min-w-0 break-words text-right type-body ${
                  signed ? `tabular-nums ${signed.cls}`
                    : d.kind === 'money' ? 'font-medium tabular-nums text-content'
                      /* A timestamp is ONE token. Letting it wrap put "PM" alone on the next line,
                         which reads as a second value rather than the tail of the first. */
                      : d.kind === 'date' ? 'whitespace-nowrap tabular-nums text-content'
                        : d.kind === 'qty' ? 'tabular-nums text-content'
                          : d.kind === 'doc' ? 'font-mono text-content' : 'text-content'}`}
                >
                  <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
                    {/*
                      * The CODE leads, as a chip — it identifies the thing; the name follows.
                      *
                      * Drawn with the app's shared CodeChip in the code's own IDENTITY HUE, the same
                      * one `WarehouseCode` uses on the register and in every picker. A locally
                      * styled grey pill would have been a second look for the same fact: WH-MUM is
                      * green on the transfers list and must not be grey here, or the colour stops
                      * being a way to recognise a warehouse and becomes decoration.
                      */}
                    {d.chip ? <CodeChip hue={entityHue(d.chip)}>{d.chip}</CodeChip> : null}
                    <DetailValue d={d}>{body}</DetailValue>
                    {/* A named person wears their role as the app's pill, right beside the name.
                        The role arrives in EITHER place: three lanes set it on the detail, every
                        other one sets it only on the link it builds for the same person. Reading
                        just `d.role` meant "Dispatched by" wore a badge while "Reports to" and
                        "Approved by" — same dialog, same kind of fact — went bare. */}
                    {(d.role ?? d.link?.role) ? <RoleBadge role={d.role ?? d.link?.role} /> : null}
                  </span>
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      {blocks.map((d, i) => {
        /*
         * LOOK AT THE EVIDENCE, don't count it.
         *
         * Verifying a deposit or a payment means deciding whether a bank really received the money,
         * and "1 file(s)" cannot answer that — it sent the approver to another screen, or, more
         * often, to approve without looking at all. The slip is shown where the decision is made,
         * full size on click, arrow keys through a set.
         */
        if (d.kind === 'attachments' && d.files?.length) {
          return (
            <div key={`${d.label}-${i}`} className="min-w-0 rounded-card bg-surface-2/70 px-3 py-2.5">
              {dialog
                ? <SectionHead icon={<Paperclip size={14} />} tint={FACT_TINT.document}>{d.label}</SectionHead>
                : <div className="mb-1.5 type-label text-subtle">{d.label}</div>}
              <AttachmentCell files={d.files} label={d.label} size={40} maxThumbs={4} />
            </div>
          );
        }
        /*
         * A DEPOSIT OF ONE SUM THAT BELONGS TO TWELVE PEOPLE.
         *
         * A rep banking a day's round hands over ₹1,00,000 that is really twelve dealers' money. The
         * one-line version had to truncate — six names and "+6 more" — and the missing six were
         * reachable from nowhere, which defeats the point of tracking whose cash it was. Given its own
         * full-width row and a scroll, every line is there, and it still totals to the deposit.
         */
        if (d.breakdown?.length) {
          const total = d.breakdown.reduce((n, x) => n + Number(x.amount ?? 0), 0);
          /*
           * A LIST, BECAUSE A SENTENCE DOES NOT SCALE.
           *
           * The transfer contents used to be joined into one string — "AGN 0.5L × 10, AGN LTE
           * 0.25L × 11" — which fits at two products and is unreadable at seven, in a right-aligned
           * half-column that would wrap it into a blob. Given the same list treatment a deposit's
           * dealers get, it reads the same whether the consignment carries two products or twenty,
           * and the footer proves it adds up.
           *
           * Quantities are NOT money: `kind: 'qty'` prints plain counts. Rendering 21 bottles as
           * ₹21.00 is worse than not showing it.
           */
          const qty = d.kind === 'qty';
          const fmt = (v: any) => (qty ? String(Number(v) || 0) : inr2(v));
          return (
            <div key={`${d.label}-${i}`} className="min-w-0">
              {dialog ? (
                <SectionHead
                  icon={qty ? <Package size={14} /> : <IndianRupee size={14} />}
                  tint={qty ? FACT_TINT.quantity : FACT_TINT.money}
                  hint={plural(d.breakdown.length, qty ? 'product' : 'item')}
                >
                  {d.label}
                </SectionHead>
              ) : (
                <div className="mb-1 type-label text-subtle">{d.label} · {d.breakdown.length}</div>
              )}
              <div className={`max-h-56 overflow-y-auto rounded-control border border-line ${dialog ? 'bg-surface shadow-e1' : ''}`}>
                <table className="w-full text-left">
                  <tbody>
                    {d.breakdown.map((x, k) => (
                      <tr key={`${x.name}-${k}`} className={`border-b border-line/50 last:border-0 ${dialog ? 'transition-colors hover:bg-surface-2/60' : ''}`}>
                        <td className={`align-top text-content ${dialog ? 'px-3 py-2 type-body-strong' : 'px-2 py-1 type-body'}`}>
                          {x.name}
                          {/* `font-mono` suits a document number under a dealer; "3 lots" is words. */}
                          {x.sub && <span className={cn('block type-caption text-subtle', !qty && 'font-mono')}>{x.sub}</span>}
                        </td>
                        <td className={`whitespace-nowrap text-right align-top type-body font-medium tabular-nums text-content ${dialog ? 'px-3 py-2' : 'px-2 py-1'}`}>
                          {fmt(x.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* The list has to add up to the deposit, and saying so is what makes it checkable. */}
                  {/* STICKY: the list scrolls at about six rows, and a total you have to scroll to
                      find is a total nobody checks — which is the whole point of showing one. */}
                  <tfoot className="sticky bottom-0">
                    <tr className={`border-t bg-surface-2 ${dialog ? (qty ? 'border-lime-600/40' : 'border-amber-500/40') : 'border-line'}`}>
                      <td className={`type-label text-muted ${dialog ? 'px-3 py-2' : 'px-2 py-1'}`}>{d.totalLabel ?? 'Total allocated'}</td>
                      <td className={`whitespace-nowrap text-right type-body font-semibold tabular-nums text-content ${dialog ? 'px-3 py-2' : 'px-2 py-1'}`}>{fmt(total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        }
        /*
         * A sentence, given the width to be one.
         *
         * Amber with a warning mark when the fact is a consequence the approver is answerable for
         * ("that order stays held until this transfer is received"); a quiet surface otherwise. The
         * tint IS the distinction — a warning that looks like a remark is a remark.
         */
        const body = bodyOf(d);
        const warn = d.kind === 'warn';
        /* In the dialog a sentence is a CALLOUT — a rail, an icon plate and its label in the callout's
           own colour — so a consequence the approver answers for cannot be skimmed as a remark. */
        if (dialog) {
          return (
            <div
              key={`${d.label}-${i}`}
              className={`flex min-w-0 items-start gap-3 rounded-card border border-l-4 px-3.5 py-3 ${
                warn
                  ? 'border-warning-500/30 border-l-warning-500 bg-warning-500/10'
                  : 'border-sky-500/25 border-l-sky-500 bg-sky-500/[0.06]'}`}
            >
              <span
                className={`grid size-8 shrink-0 place-items-center rounded-full ${
                  warn ? 'bg-warning-500/20 text-warning-600 dark:text-harvest-300' : 'bg-sky-500/15 text-sky-600 dark:text-sky-400'}`}
                aria-hidden
              >
                {warn ? <TriangleAlert size={16} /> : <Info size={16} />}
              </span>
              <div className="min-w-0">
                <div className={`type-label-strong ${warn ? 'text-warning-600 dark:text-harvest-300' : 'text-sky-600 dark:text-sky-400'}`}>{d.label}</div>
                <div className="mt-0.5 break-words type-body text-content">
                  <DetailValue d={d}>{body}</DetailValue>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div
            key={`${d.label}-${i}`}
            className={`flex min-w-0 items-start gap-2.5 rounded-card px-3 py-2.5 ${
              warn
                ? 'bg-harvest-400/12 text-harvest-700 dark:bg-harvest-400/[0.10] dark:text-harvest-300'
                : 'bg-surface-2/70 text-content'}`}
          >
            <span className={`mt-0.5 shrink-0 ${warn ? '' : 'text-subtle'}`}>
              {warn ? <TriangleAlert size={15} /> : <Info size={15} />}
            </span>
            <div className="min-w-0">
              <div className={`type-label ${warn ? 'opacity-80' : 'text-subtle'}`}>{d.label}</div>
              <div className="mt-0.5 break-words type-body">
                <DetailValue d={d}>{body}</DetailValue>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * APPROVALS — one page for every decision the business is waiting on.
 *
 * Before this, "what is waiting on me?" meant opening seven screens and knowing which of them had a
 * queue. Each lane still owns its own rules; this page owns the QUESTION. It shows a lane only when
 * the viewer holds that lane's own approve permission, and each row's buttons come from the API,
 * so nothing is offered that the endpoint would then refuse.
 *
 * The second tab is the Super Admin's: recently DECIDED records, and the power to send one back.
 */
export function ApprovalsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const meId = useAuthStore((st) => st.user?.id);
  // Every effective role, not just the primary one: a domain exemption (a Super Admin closing a
  // complaint they resolved) has to hold for a user who carries that role as a second hat too.
  // Derived through useMemo rather than returned from the selector — a selector that builds a new
  // array each call gives zustand a snapshot that never compares equal.
  const meRoles = useAuthStore((st) => st.user?.roles);
  const meRole = useAuthStore((st) => st.user?.role);
  const myRoles = useMemo(() => (meRoles?.length ? meRoles : meRole ? [meRole] : []), [meRoles, meRole]);
  const [tab, setTab] = useUrlTab('tab', ['waiting', 'mine', 'decided'] as const, 'waiting');
  const [lane, setLane] = useUrlString('lane', 'ALL');
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 250);
  const [decideFor, setDecideFor] = useState<{ item: Item; action: LaneAction } | null>(null);
  const [waitingFor, setWaitingFor] = useState<Item | null>(null);   // a queued record opened for reading
  const [revertFor, setRevertFor] = useState<Decided | null>(null);
  /* The decided row a reader asked to look at. The table gives six columns; this opens the whole
     decision, and the way back to the record it was made on. */
  const [decidedFor, setDecidedFor] = useState<Decided | null>(null);
  const [revertReason, setRevertReason] = useState('');

  const { data: meta, isLoading: metaLoading } = useQuery({
    queryKey: ['approvals', 'lanes'],
    queryFn: () => request<{ canRevert: boolean; lanes: Lane[] }>({ url: '/approvals/lanes' }),
  });
  const lanes = meta?.lanes ?? [];
  const canRevert = !!meta?.canRevert;
  const laneMap = useMemo(() => new Map(lanes.map((l) => [l.key, l])), [lanes]);


  /**
   * Fill a lane's link template from what a row actually knows.
   *
   * Lane links are templates — mostly `{id}`, but several name another key the ROW carries in its
   * `meta` (a cancellation request links to its ORDER; a deposit links to the bank account whose
   * statement it sits on). Substituting only `{id}` navigated a reader to the literal path
   * `/orders/{orderId}`, which is not a page. Anything left unfilled returns null, and the caller
   * hides the button rather than promising a URL with a brace in it.
   */
  const fillLink = (tpl: string, values: Record<string, any>): string | null => {
    const href = tpl.replace(/\{(\w+)\}/g, (whole, key) => {
      const v = values[key];
      return v == null || v === '' ? whole : encodeURIComponent(String(v));
    });
    return href.includes('{') ? null : href;
  };

  /**
   * An in-place action's URL: the record's link plus the act to open there.
   *
   * A lane whose link already names the act keeps it. The wrong-batch lane links `act=resolve`, the
   * dialog the register opens for a lot decision, and appending the action key as well sent
   * `act=resolve&act=confirm` (DEF-072).
   */
  const withAct = (href: string, key: string): string =>
    /[?&]act=/.test(href) ? href : `${href}${href.includes('?') ? '&' : '?'}act=${encodeURIComponent(key)}`;

  /**
   * The screen a PENDING row opens on — the record itself, not the list it lives in.
   *
   * A reversal request is the one row that is ABOUT another record rather than being one: it has no
   * screen of its own, so it borrows the target lane's, which is the record the reader is being
   * asked to rule on.
   */
  const rowHref = (r: Item): string | null => {
    const lane = laneMap.get(r.domain);
    const meta = (r.meta ?? {}) as Record<string, any>;
    if (lane?.link) return fillLink(lane.link, { ...meta, id: r.id });
    const target = meta.targetDomain ? laneMap.get(String(meta.targetDomain)) : undefined;
    if (target?.link && meta.targetId) return fillLink(target.link, { ...meta, id: meta.targetId });
    return null;
  };

  /**
   * The screen this DECIDED decision was made on, if the lane declares one.
   *
   * A decided row carries only the record's own id — no `meta` — so a template asking for anything
   * else cannot be filled and the button stays hidden.
   */
  const recordHref = (r: Decided): string | null => {
    const tpl = laneMap.get(r.domain)?.link;
    return tpl ? fillLink(tpl, { id: r.id }) : null;
  };

  /*
   * Sorting is the SERVER's answer, not the browser's: the table reports a header click, we push it
   * as `?sort=-field`, and the API returns rows already ordered against its own whitelist. Two people
   * looking at the same queue therefore see the same list — and the ordering survives the fact that
   * these rows are stitched together from fifteen different tables.
   */
  /* ASCENDING is longest-waiting-first now that `waiting` sorts on the raised-at INSTANT rather than
     on a whole number of days (see QUEUE_SORTS). Same rows in the same order as before; the arrow
     now points the way the dates in the column actually run. */
  const { sortState, onSortChange, sort } = useServerSort({ defaultSort: { key: 'waiting', dir: 'asc' } });

  const { data: queue, isLoading } = useQuery({
    queryKey: ['approvals', 'queue', sort ?? ''],
    queryFn: () => request<Item[]>({ url: '/approvals', params: sort ? { sort } : {} }),
    placeholderData: keepPreviousData,
  });
  const all = useMemo(() => queue ?? [], [queue]);

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return all.filter((r) => {
      if (lane !== 'ALL' && r.domain !== lane) return false;
      if (!s) return true;
      return [r.docNo, r.title, r.subtitle, r.raisedByName, laneMap.get(r.domain)?.label]
        .filter(Boolean).join(' ').toLowerCase().includes(s);
    });
  }, [all, lane, search, laneMap]);

  /*
   * `?focus=<id>` opens the row the link meant, expanded and scrolled to.
   *
   * Every deep link into this hub already carried one — the notification dispatcher sends
   * `/approvals?lane=<key>&focus=<ref id>` — but this page never read the param, so all of them
   * dropped the reader at the top of the lane to hunt for their own row. The key must match the
   * table's own `rowKey` (`<domain>:<id>`), or the expand and the scroll both miss.
   */
  const focus = useFocusRow(rows, {
    keyOf: (r: Item) => `${r.domain}:${r.id}`,
    // A notification link names ONE record, so it opens that record's summary. The row underneath is
    // still highlighted and scrolled to, which is what tells the reader WHERE in the queue they are.
    onFocus: (r: Item) => setWaitingFor(r),
  });

  const counts = useMemo(() => {
    const m = new Map<string, { n: number; oldest: number }>();
    for (const r of all) {
      const c = m.get(r.domain) ?? { n: 0, oldest: 0 };
      c.n += 1; c.oldest = Math.max(c.oldest, r.ageDays);
      m.set(r.domain, c);
    }
    return m;
  }, [all]);

  const oldestOverall = useMemo(() => all.reduce((m, r) => Math.max(m, r.ageDays), 0), [all]);

  /** Lanes with work first (most overdue, then biggest); empty lanes keep their group order at the end. */
  /*
   * GROUP first, then urgency within the group.
   *
   * This used to sort by "has work" and then by age, with the group only as a last tiebreak — so a
   * Money lane sat between two Field-force ones and the colour coding became decoration: every tile
   * carries its group name and tint, and none of them were next to their own kind. Sorting by group
   * makes the rail scannable ("where are my stock decisions?") and the colours meaningful again,
   * while inside each group the lane that needs a person still comes first.
   */
  /** Empty lanes are collapsed by default; the toggle under the rail says how many there are. */
  const [showEmptyLanes, setShowEmptyLanes] = useState(false);

  const railLanes = useMemo(() => {
    const rank = (l: Lane) => GROUP_ORDER.indexOf(l.group);
    return [...lanes].sort((a, b) => {
      const g = rank(a) - rank(b);
      if (g !== 0) return g;
      const ca = counts.get(a.key); const cb = counts.get(b.key);
      const na = ca?.n ?? 0; const nb = cb?.n ?? 0;
      // Lanes with work lead their group, longest-waiting first; empty lanes trail it.
      if ((na > 0) !== (nb > 0)) return nb - na;
      return (cb?.oldest ?? 0) - (ca?.oldest ?? 0) || nb - na || a.label.localeCompare(b.label);
    });
  }, [lanes, counts]);

  /** How many lanes are sitting at zero — named in the toggle so the fact survives the collapse. */
  const emptyLaneCount = useMemo(
    () => railLanes.filter((l) => (counts.get(l.key)?.n ?? 0) === 0 && lane !== l.key).length,
    [railLanes, counts, lane],
  );

  // ── the SA's "did we get that right?" list ──
  /**
   * The caller's OWN work that came back. Fetched for everyone — this is the half of the page that
   * exists for people with no approval permission at all, so it is never gated on one.
   */
  const { data: myWork, isLoading: mineLoading } = useQuery({
    queryKey: ['approvals', 'mine'],
    queryFn: () => request<MyWork[]>({ url: '/approvals/mine' }),
    placeholderData: keepPreviousData,
  });
  const mine = useMemo(() => myWork ?? [], [myWork]);
  const returnedCount = useMemo(() => mine.filter((m) => m.outcome === 'RETURNED').length, [mine]);

  const decidedSort = useServerSort({ urlKey: 'dsort', defaultSort: { key: 'decidedAt', dir: 'desc' } });
  const { data: decided, isLoading: decidedLoading } = useQuery({
    queryKey: ['approvals', 'decided', decidedSort.sort ?? ''],
    queryFn: () => request<Decided[]>({
      url: '/approvals/decided', params: { days: 30, ...(decidedSort.sort ? { sort: decidedSort.sort } : {}) },
    }),
    enabled: canRevert && tab === 'decided',
    placeholderData: keepPreviousData,
  });

  const revert = useConfirmedMutation({ title: "Revert this approval?", content: "The record goes back to its previous state, and the reversal is recorded against your name in the audit trail.", okText: "Yes, revert", danger: true }, {
    mutationFn: () => request<any>({
      url: `/approvals/${revertFor?.domain}/${revertFor?.id}/revert`,
      method: 'POST', data: { reason: revertReason.trim() },
    }),
    onSuccess: (res: any) => {
      const warn: string[] = res?.warnings ?? [];
      message.success(res?.message ?? 'Sent back for review');
      if (warn.length) warn.forEach((w) => message.warning(w, 10));
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      setRevertFor(null); setRevertReason('');
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? e?.message ?? 'Could not send it back'),
  });

  // ── columns ──
  const waitingColumns = [
    {
      title: 'Type', key: 'type', dataIndex: 'domain', width: 168, sortable: true,
      render: (v: string) => <TypeChip group={laneMap.get(v)?.group}>{laneMap.get(v)?.label ?? v}</TypeChip>,
    },
    {
      title: 'Document', key: 'docNo', dataIndex: 'docNo', width: 160, sortable: true,
      /* The document number is the handle people reach for, so it goes straight to the record's own
         page (an order number opens the order). Only a lane with no page to go to falls back to the
         summary. The ROW stays inert (see the table below); this is a deliberate target, not a stray
         click on a list being worked. It WRAPS rather than truncating: in a 160px column a long number
         lost its last characters, and a document number with its tail cut off is not a number anyone
         can find. */
      render: (v: string | null, r: Item) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); const href = rowHref(r); if (href) navigate(href); else setWaitingFor(r); }}
          title="Open this record"
          aria-label={`Open ${v ?? r.title}`}
          className="block max-w-full text-left font-medium text-brand-700 [overflow-wrap:anywhere] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-400"
        >
          {/* Never the row id: it is not the record's name, and the same "#21" names a different
              record in every other lane sharing this queue. */}
          {v ?? <span className="text-subtle">—</span>}
        </button>
      ),
    },
    {
      /*
       * A WIDTH here even though this is the flexible-feeling column.
       *
       * The table is `layoutFixed`, and a column with no declared width absorbs ALL the leftover
       * space — so on a wide screen this one grew to a dead band while Decision stayed cramped and
       * "Raised by" wrapped. With every column sized, the browser scales them PROPORTIONALLY to
       * fill the table, and the slack is shared instead of dumped in one place.
       */
      title: 'What', key: 'title', dataIndex: 'title', width: 340, sortable: true,
      render: (v: string, r: Item) => (
        <div className="min-w-0">
          <WhatTitle title={v} meta={r.meta} />
          {r.subtitle ? <div className="truncate type-caption text-subtle">{r.subtitle}</div> : null}
        </div>
      ),
    },
    {
      /*
       * HOW MUCH IS ON IT — a count, not a sentence.
       *
       * A stock movement has no rupee value, so its Value cell is always a dash; what it HAS is a
       * number of lines and a number of units. Those were being squeezed into the row's subtitle as
       * "AGN x 5 +3 more", which says almost nothing about a four-product consignment and eats the
       * room the row needs for why it exists. Given the same column the transfers register gives it,
       * beside the Value it stands in for. Every other lane shows a dash here, exactly as the stock
       * lanes do under Value.
       */
      title: 'Items', key: 'items', width: 96, align: 'right' as const,
      render: (_v: any, r: Item) => (r.meta?.items == null ? <span className="text-subtle">&mdash;</span> : (
        <div className="leading-tight">
          <div className="tabular-nums text-content">{r.meta.items}</div>
          {r.meta.units != null && (
            <div className="type-caption tabular-nums text-subtle">{r.meta.units} units</div>
          )}
        </div>
      )),
    },
    {
      title: 'Value', key: 'amount', dataIndex: 'amount', align: 'right' as const, width: 120, sortable: true,
      render: (v: number | null) => (v == null ? <span className="text-subtle">—</span> : <b>{inr(v)}</b>),
    },
    {
      title: 'Raised by', key: 'raisedBy', dataIndex: 'raisedByName', width: 190, sortable: true,
      render: (v: string | null, r: Item) => <Person name={v} role={r.raisedByRole} />,
    },
    {
      // The actual date AND time it started waiting — "61d" alone never answers "since when?".
      title: 'Waiting since', key: 'waiting', dataIndex: 'raisedAt', width: 190, sortable: true,
      render: (v: string | null, r: Item) => (
        <div className="min-w-0">
          <div className="whitespace-nowrap text-content">{v ? formatIst(v) : <span className="text-subtle">—</span>}</div>
          <div className="mt-0.5"><Pill color={ageTone(r.ageDays)}>{r.ageDays === 0 ? 'today' : plural(r.ageDays, 'day')}</Pill></div>
        </div>
      ),
    },

    {
      title: 'Decision', width: 190,
      render: (_: any, r: Item) => {
        const l = laneMap.get(r.domain);
        // Only the decisions this row can actually take, in the state it is actually in —
        // see actionApplies() above.
        const actions = (l?.actions ?? []).filter((a) => actionApplies(a, r, meId, myRoles));
        const href = rowHref(r);
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {actions.map((a) => (
              <Tooltip key={a.key} label={a.label}>
                <Button
                  size="sm"
                  variant={a.danger ? 'soft-danger' : a.key === 'approve' ? 'primary' : 'default'}
                  aria-label={`${a.label} ${r.docNo ?? r.title}`}
                  icon={actionIcon(r.domain, a.key)}
                  onClick={() => {
                    /*
                     * A decision that needs context this page cannot show (batch picking, a
                     * line-by-line goods receipt) opens on its own screen instead of pretending.
                     *
                     * It carries the ACT with it (`&act=<action key>`), because the lane's link only
                     * names the RECORD. Without it the destination page can do no better than open
                     * that record for reading — which is exactly what "Pick batches" started doing
                     * when the transfers register moved its row detail into a read-only dialog: the
                     * approver arrived at a description of the transfer with a Close button, one
                     * click further from the job than before they pressed anything.
                     *
                     * A page that does not understand the act simply ignores the parameter and shows
                     * the record, i.e. exactly today's behaviour — so this cannot break a lane that
                     * has not opted in.
                     */
                    if (a.openInPlace) {
                      if (href) navigate(withAct(href, a.key));
                      return;
                    }
                    setDecideFor({ item: r, action: a });
                  }}
                />
              </Tooltip>
            ))}
            {href ? (
              <Tooltip label="Open the record">
                <Button size="sm" variant="ghost" aria-label={`Open ${r.docNo ?? r.title}`} icon={<Eye size={16} />}
                  onClick={() => navigate(href)} />
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
  ];

  const decidedColumns = [
    {
      title: 'Type', key: 'type', dataIndex: 'laneLabel', width: 168, sortable: true,
      render: (v: string, r: Decided) => <TypeChip group={laneMap.get(r.domain)?.group}>{v}</TypeChip>,
    },
    {
      title: 'Document', key: 'docNo', dataIndex: 'docNo', width: 170, sortable: true,
      /* A document number is the handle people reach for, so it should behave like one. It read as
         plain text while the only interactive thing on the row was "send back" — the destructive
         action — which is the wrong thing to be the only way in. */
      render: (v: string | null, r: Decided) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setDecidedFor(r); }}
          title="See this decision in full"
          aria-label={`See the ${r.laneLabel.toLowerCase()} decision${v ? ` on ${v}` : ''} in full`}
          className="block max-w-full text-left font-mono text-xs text-brand-700 [overflow-wrap:anywhere] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-400"
        >
          {/* A row id is not a document number: it is not what the record is called anywhere else,
              and "#21" belongs to a different record in every other lane on this same table. */}
          {v ?? <span className="text-subtle">—</span>}
        </button>
      ),
    },
    /*
     * Sized like every other column: with `layoutFixed` a width-less column swallows ALL the
     * leftover space, which is why this one stretched into a blank band while Decided by wrapped.
     *
     * 300 → 210 when the initials disc came off. `layoutFixed` shares the table's slack in
     * PROPORTION to the declared widths, so the widest column takes the largest share of it — and
     * this one, holding a single short name, was opening a hand's width of nothing before Value
     * while Value and Outcome were pressed together at the other end of it.
     */
    {
      title: 'Who / what', key: 'party', dataIndex: 'party', width: 210, sortable: true,
      render: (v: string | null, r: Decided) => (
        <PartyCell name={v} kind={r.partyKind} id={r.partyId} role={r.partyRole}
          sub={r.partySub ? formatIstDate(r.partySub) : null} />
      ),
    },
    /* A right-aligned number and a left-aligned pill meet at the column boundary, so the cells' own
       padding is the only thing holding them apart — and ₹5,000 sat all but touching APPROVED. The
       gap is put on the CONTENT (the table takes no per-column class) so the figure keeps its right
       alignment and simply stops short of the verdict on it. */
    {
      title: 'Value', key: 'amount', dataIndex: 'amount', align: 'right' as const, width: 130, sortable: true,
      render: (v: number | null) => (v == null
        ? <span className="pr-3 text-subtle">—</span>
        : <b className="pr-3">{inr(v)}</b>),
    },
    { title: 'Outcome', key: 'status', dataIndex: 'status', width: 130, sortable: true, render: (v: string) => <Pill color={v === 'REJECTED' ? 'red' : v === 'CLOSED' ? 'purple' : 'green'}>{v}</Pill> },
    {
      title: 'Decided by', key: 'decidedBy', dataIndex: 'decidedBy', width: 200, sortable: true,
      /* The moment moved out to its own column — a name and a date are two different facts, and
         "an hour ago" was standing in for a timestamp this table never actually showed. */
      render: (v: string | null, r: Decided) => <Person name={v} role={r.decidedByRole} />,
    },
    {
      /*
       * WHEN, exactly — the same treatment "Waiting since" gets on the other tab.
       *
       * This column read "an hour ago" and nothing else, tucked under the decider's role pill. A
       * relative time answers "is this fresh?" and nothing else: it cannot be compared between two
       * rows, cannot be quoted into a message or a dispute, and rounds a decision taken at 01:40 PM
       * into "a day ago" by morning. The timestamp leads and the relative age stays underneath it,
       * so both questions are answered and neither is guessed at.
       */
      title: 'Decided on', key: 'decidedAt', dataIndex: 'decidedAt', width: 185, sortable: true,
      render: (v: string | null) => (
        <div className="min-w-0">
          <div className="whitespace-nowrap text-content">{v ? formatIst(v) : <span className="text-subtle">—</span>}</div>
          {v && <div className="mt-0.5 type-caption text-subtle">{formatIstRelative(v)}</div>}
        </div>
      ),
    },
    {
      /*
       * Not offered on YOUR OWN decisions.
       *
       * Send back is an oversight power: it overturns somebody else's call and, as the banner above
       * this table says, records the reason permanently against the person whose decision was
       * overturned. Pointed at yourself that sentence means nothing, and the button was drawn on
       * every row a Super Admin had just decided — inviting them to formally overrule themselves.
       */
      title: 'Send back', width: 130,
      render: (_: any, r: Decided) => {
        const mine = !!meId && !!r.decidedById && String(meId) === String(r.decidedById);
        if (mine) return <span className="text-subtle" title="This was your decision">—</span>;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Tooltip label={`Send back for review (→ ${r.revertTo})`}>
              <Button size="sm" variant="soft-danger" aria-label={`Send ${r.docNo ?? `this ${r.laneLabel.toLowerCase()}`} back for review`}
                icon={<Undo2 size={16} />} onClick={() => { setRevertFor(r); setRevertReason(''); }} />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const mineColumns = [
    {
      title: 'Type', key: 'type', dataIndex: 'domain', width: 168, sortable: true,
      render: (v: string) => <TypeChip group={laneMap.get(v)?.group ?? LANE_GROUP_FALLBACK[v]}>{LANE_LABEL_FALLBACK[v] ?? v}</TypeChip>,
    },
    {
      title: 'Document', key: 'docNo', dataIndex: 'docNo', width: 160, sortable: true,
      render: (v: string | null) => <span className="font-medium">{v ?? <span className="text-subtle">—</span>}</span>,
    },
    {
      /*
       * A WIDTH here even though this is the flexible-feeling column.
       *
       * The table is `layoutFixed`, and a column with no declared width absorbs ALL the leftover
       * space — so on a wide screen this one grew to a dead band while Decision stayed cramped and
       * "Raised by" wrapped. With every column sized, the browser scales them PROPORTIONALLY to
       * fill the table, and the slack is shared instead of dumped in one place.
       */
      title: 'What', key: 'title', dataIndex: 'title', width: 340, sortable: true,
      render: (v: string, r: MyWork) => (
        <div className="min-w-0">
          <div className="truncate text-content">{v}</div>
          {r.subtitle ? <div className="truncate type-caption text-subtle">{r.subtitle}</div> : null}
        </div>
      ),
    },
    {
      title: 'Value', key: 'amount', dataIndex: 'amount', align: 'right' as const, width: 120, sortable: true,
      render: (v: number | null) => (v == null ? <span className="text-subtle">—</span> : <b>{inr(v)}</b>),
    },
    {
      title: 'Outcome', key: 'outcome', dataIndex: 'outcome', width: 130, sortable: true,
      render: (v: MyWork['outcome']) => (
        <Pill color={v === 'RETURNED' ? 'orange' : 'red'}>{v === 'RETURNED' ? 'Sent back' : 'Rejected'}</Pill>
      ),
    },
    {
      title: 'Decided by', key: 'decidedBy', dataIndex: 'decidedByName', width: 200,
      render: (v: string | null, r: MyWork) => <Person name={v} role={r.decidedByRole} />,
    },
    {
      title: 'When', key: 'waiting', dataIndex: 'decidedAt', width: 190, sortable: true,
      render: (v: string | null, r: MyWork) => (
        <div className="min-w-0">
          <div className="whitespace-nowrap text-content">{v ? formatIst(v) : <span className="text-subtle">—</span>}</div>
          <div className="mt-0.5"><Pill color={ageTone(r.ageDays)}>{r.ageDays === 0 ? 'today' : plural(r.ageDays, 'day')}</Pill></div>
        </div>
      ),
    },
    {
      title: 'Open', width: 80,
      render: (_: any, r: MyWork) => (
        <div onClick={(e) => e.stopPropagation()}>
          {r.link ? (
            <Tooltip label="Open it and fix it">
              <Button size="sm" variant="default" aria-label={`Open ${r.docNo ?? r.title}`}
                icon={<ExternalLink size={16} />} onClick={() => navigate(r.link!)} />
            </Tooltip>
          ) : null}
        </div>
      ),
    },
  ];

  const activeLane = lane === 'ALL' ? null : laneMap.get(lane);

  if (metaLoading) return <div className="p-1"><Skeleton className="mb-4 h-20" /><Skeleton className="h-64" /></div>;

  // Only a true dead end — no lanes to approve AND nothing of your own came back.
  if (!lanes.length && !mine.length) {
    return (
      <>
        <PageHeader title="Approvals" subtitle="Decisions waiting on you, and your own work that came back." />
        <EmptyState
          title="Nothing needs you here"
          description="This page shows the decisions you are allowed to take, and anything you raised that was sent back to you. Right now there is neither."
        />
      </>
    );
  }

  return (
    <>
      {/*
        * The counts sit BESIDE the heading, not in the actions slot.
        *
        * As a right-aligned action they ended up a metre from the word they qualify on a wide
        * screen — "82" floating alone at the far edge, with nothing next to it to say 82 of what.
        * Against the title they read as one phrase: "Approvals · 82 waiting on you".
        */}
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="truncate">Approvals</span>
            {lanes.length ? (
              <Pill color={all.length ? (all.some((r) => r.ageDays >= 7) ? 'red' : 'gold') : 'default'}>
                {all.length} waiting on you
              </Pill>
            ) : null}
            {returnedCount ? <Pill color="orange">{returnedCount} back with you</Pill> : null}
          </span>
        }
        subtitle="Decisions waiting on you, and your own work that came back — longest-waiting first."
      />

      {/*
        Lane rail. A UNIFORM grid rather than per-group rows: every tile is the same size, so the
        block stays rectangular whatever mix of lanes a role holds, and the kind of work is carried
        by colour instead of by headings that floated at different heights.

        Lanes are ordered by GROUP first (see railLanes) so each colour runs in one band, and within
        a group the lane with work — longest-waiting first — leads. Empty lanes stay visible (dimmed)
        so "nothing waiting" is a fact you can see rather than a lane you assume is missing.
      */}
      {lanes.length ? (
      <Card className="mb-4 p-3">
        {/* Named so the rail is addressable as one thing — it is a filter group, and the tiles are
            otherwise indistinguishable from the nav bar's own count buttons. */}
        <div role="group" aria-label="Approval lanes"
          className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
          <button
            type="button"
            onClick={() => setLane('ALL')}
            aria-pressed={lane === 'ALL'}
            className={`flex h-[92px] w-full flex-col justify-between rounded-card border px-3 py-2 text-left transition
              ${lane === 'ALL' ? 'border-brand-500 bg-brand-500/10 shadow-sm' : 'border-line bg-surface-1 hover:border-brand-500/60'}`}
          >
            <span className="type-overline text-subtle">All types</span>
            <span className="type-label leading-4 text-content">Everything</span>
            <span className="flex items-baseline gap-2">
              <span className="type-metric leading-none text-content">{all.length}</span>
              {oldestOverall > 0 ? <Pill color={ageTone(oldestOverall)}>{oldestOverall}d</Pill> : null}
            </span>
          </button>
          {(showEmptyLanes ? railLanes : railLanes.filter((l) => (counts.get(l.key)?.n ?? 0) > 0 || lane === l.key)).map((l) => {
            const c = counts.get(l.key);
            return (
              <LaneTile
                key={l.key}
                group={l.group}
                label={l.label}
                count={c?.n ?? 0}
                oldestDays={c?.oldest ?? 0}
                active={lane === l.key}
                muted={!c?.n}
                title={`${l.waitingFor}

Until it is decided: ${l.blocks}`}
                onClick={() => setLane(lane === l.key ? 'ALL' : l.key)}
              />
            );
          })}
        </div>
        {/*
          * Empty lanes are still a FACT the reader is entitled to — the rail was built to show that
          * "nothing is waiting" rather than leave a lane looking absent. But two thirds of the wall
          * standing at zero buries the handful that need someone, so the fact is kept as a sentence
          * and the tiles are one click away. The lane currently being filtered always stays on screen,
          * even at zero, or the page would appear to have no filter applied.
          */}
        {emptyLaneCount > 0 ? (
          <button
            type="button"
            onClick={() => setShowEmptyLanes((v) => !v)}
            className="mt-2 type-caption text-muted underline-offset-2 hover:text-content hover:underline"
          >
            {showEmptyLanes
              ? `Hide the ${emptyLaneCount} ${emptyLaneCount === 1 ? 'lane' : 'lanes'} with nothing waiting`
              : `${emptyLaneCount} ${emptyLaneCount === 1 ? 'lane has' : 'lanes have'} nothing waiting — show`}
          </button>
        ) : null}
      </Card>
      ) : null}

      {activeLane ? (
        <Alert
          type="info"
          className="mb-4"
          message={activeLane.waitingFor}
          description={<span className="type-caption">Until it is decided: {activeLane.blocks}</span>}
        />
      ) : null}

      <Tabs
        activeKey={lanes.length ? tab : 'mine'}
        onChange={setTab}
        items={[
          // A non-approver holds no lanes, so this tab would be a permanently empty page for them.
          ...(lanes.length ? [{
            key: 'waiting',
            label: `Waiting on a decision${all.length ? ` (${all.length})` : ''}`,
            children: (
              <Card className="mt-3">
                <TwDataTable
                  storageKey="approvals-waiting"
                  rowKey={(r: Item) => `${r.domain}:${r.id}`}
                  loading={isLoading}
                  columns={waitingColumns as any}
                  dataSource={rows}
                  sortState={sortState}
                  onSortChange={onSortChange}
                  /*
                   * CLICKING THE ROW DOES NOTHING — on purpose (user, 2026-09-15: "remove the modal when
                   * I click on the row"). A stray click on a queue row used to throw a dialog over the
                   * very list being worked. The row's own buttons are the actions: approve / reject /
                   * send back decide it in place, and the eye opens the record itself. The record's summary
                   * opens only from a deliberate target: its document number, or a notification deep link
                   * (`?focus=`).
                   */
                  rowClassName={focus.rowClassName}
                  layoutFixed
                  pagination={{ pageSize: 25, showTotal: (t) => `${t} waiting` }}
                  emptyText={
                    /*
                     * "Nothing is waiting on you" is only true when NOTHING IS FILTERED. Said over a
                     * search that simply missed, it is a lie about the state of the business: a
                     * reader who followed a notification here, typed the document number and got
                     * this reasonably concluded the record had vanished, while 96 items sat behind
                     * the filter. An empty result has to name the thing that emptied it.
                     */
                    <EmptyState
                      title={
                        search ? `Nothing waiting matches "${search}"`
                          : lane === 'ALL' ? 'Nothing is waiting on you'
                            : 'Nothing waiting in this lane'
                      }
                      description={
                        search
                          ? all.length > 0
                            ? `${all.length} thing${all.length === 1 ? ' is' : 's are'} still waiting — this search just does not match ${lane === 'ALL' ? 'any of them' : 'anything in this lane'}. A record that has already been decided, or that is waiting on somebody else (a complaint sitting with the customer, say), is not in this queue — look for it on its own screen.`
                            : 'Nothing is waiting on you at all, so there is nothing here to match.'
                          : lane === 'ALL'
                            ? 'Every decision in your lanes has been taken. New work will appear here as it is raised.'
                            : 'Pick another lane, or "Everything", to see the rest of the queue.'
                      }
                      action={search || lane !== 'ALL'
                        ? <FilterResetButton onClick={() => { setSearchInput(''); setLane('ALL'); }} />
                        : undefined}
                    />
                  }
                  toolbarLeft={
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        className="w-64"
                        placeholder="Search document, retailer, rep…" aria-label="Search document, retailer, rep"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        prefix={<Search size={14} />}
                      />
                      {/*
                        * NO lane dropdown here. The rail of tiles above IS the type filter — it
                        * carries every lane, its count and its age, and clicking one filters this
                        * table. A second control listing the same twenty-seven lanes put the same
                        * figures on screen twice and made a farmer queue offer "Order approval" in
                        * its filter; the reader picks the lane where they can see the numbers.
                        */}
                      {/* size="md" matches the 36px search box and lane picker beside it. */}
                      {(searchInput || lane !== 'ALL') ? (
                        <FilterResetButton size="md" onClick={() => { setSearchInput(''); setLane('ALL'); }} />
                      ) : null}
                    </div>
                  }
                />
              </Card>
            ),
          }] : []),
          ...(mine.length ? [{
            key: 'mine',
            label: `Back with you${returnedCount ? ` (${returnedCount})` : ''}`,
            children: (
              <Card className="mt-3">
                <Alert
                  type={returnedCount ? 'warning' : 'info'}
                  className="mb-3"
                  message={returnedCount
                    ? `${returnedCount} thing${returnedCount === 1 ? '' : 's'} you raised came back to you`
                    : 'Nothing is waiting on you here'}
                  description="Sent back means it is in your hands, not your approver's — nobody else is looking at it until you re-submit. Rejected items are shown for 30 days so the decision and its reason are not lost."
                  icon={<Undo2 size={18} />}
                />
                <TwDataTable
                  storageKey="approvals-mine"
                  rowKey={(r: MyWork) => `${r.domain}:${r.id}`}
                  loading={mineLoading}
                  columns={mineColumns as any}
                  dataSource={mine}
                  layoutFixed
                  pagination={{ pageSize: 25, showTotal: (t) => plural(t, 'item') }}
                  expandable={{
                    expandRowByClick: true,
                    expandedRowRender: (r: MyWork) => (
                      <DetailGrid
                        details={[
                          { label: 'What was said', value: r.reason ?? 'no reason was recorded', kind: (r.reason ? undefined : 'warn') as any },
                          { label: 'What to do next', value: r.nextStep },
                          /* Name + pill, not "Priya Accounts · accounts": a role glued to a name with
                             a middot is the raw enum wearing a disguise. */
                          { label: 'Decided by', value: r.decidedByName, role: r.decidedByRole },
                          { label: 'Decided', value: r.decidedAt, kind: 'date' },
                          { label: 'Current status', value: r.status },
                          { label: 'Value', value: r.amount, kind: 'money' },
                        ]}
                      />
                    ),
                  }}
                  emptyText={<EmptyState title="Nothing came back to you" description="Anything you raise that is sent back or turned down will appear here with the reason." />}
                />
              </Card>
            ),
          }] : []),
          ...(canRevert ? [{
            key: 'decided',
            label: 'Recently decided',
            children: (
              <Card className="mt-3">
                <Alert
                  type="warning"
                  className="mb-3"
                  message="Sending a decision back is a Super Admin power"
                  description="It unwinds what the approval actually did — a retailer-ledger credit, a stock movement, a wallet payout — and puts the record back in its approver's queue. The reason you give is kept permanently against the person whose decision was overturned."
                  icon={<ShieldAlert size={18} />}
                />
                <TwDataTable
                  storageKey="approvals-decided"
                  rowKey={(r: Decided) => `${r.domain}:${r.id}`}
                  loading={decidedLoading}
                  columns={decidedColumns as any}
                  dataSource={decided ?? []}
                  onRow={(r: Decided) => ({ onClick: () => setDecidedFor(r), style: { cursor: 'pointer' } })}
                  sortState={decidedSort.sortState}
                  onSortChange={decidedSort.onSortChange}
                  layoutFixed
                  pagination={{ pageSize: 25, showTotal: (t) => `${t} decided in the last 30 days` }}
                  emptyText={<EmptyState title="Nothing decided in the last 30 days" description="Decisions taken across every workflow show up here, newest first." />}
                />
              </Card>
            ),
          }] : []),
        ]}
      />

      <DecisionModal
        open={!!decideFor}
        onClose={() => setDecideFor(null)}
        action={decideFor?.action ?? null}
        item={decideFor?.item ?? null}
        /* Whatever the lane marked as evidence rides into the dialog, so the proof is on screen at the
           moment the decision is committed rather than behind it. */
        evidence={(decideFor?.item?.details ?? [])
          .filter((d: Detail) => d.kind === 'attachments' && d.files?.length)
          .map((d: Detail) => ({ label: d.label, files: d.files ?? [] }))}
        /* Everything the expanded row was showing, minus what is already rendered as evidence or as a
           breakdown table — the dialog covers the row, so the facts have to come with it. */
        facts={(decideFor?.item?.details ?? [])
          .filter((d: Detail) => d.kind !== 'attachments' && !d.breakdown?.length
            && d.value !== null && d.value !== undefined && d.value !== '')
          .map((d: Detail) => ({ label: d.label, value: d.value, kind: d.kind }))}
        onDone={() => qc.invalidateQueries({ queryKey: ['approvals'] })}
      />

      {/*
        * THE QUEUED RECORD, OPENED FOR READING — from its document number or a notification link.
        *
        * Dressed in its LANE's colour and glyph (the wash the lane tile and the type chip already carry),
        * so it reads as the row it came from rather than a neutral sheet that could belong to any queue.
        * A movement draws its route as two warehouse ends in their identity hues; every other lane leads
        * with its title. The decisions come with it, so a reader convinced by the evidence does not have
        * to close this and find the row again.
        */}
      <Modal
        open={!!waitingFor}
        onOpenChange={(o) => { if (!o) setWaitingFor(null); }}
        /* Wide on purpose: facts sit two-up as tiles, and a consignment's contents is a list that grows
           with the transfer. */
        width={1040}
        /* Nothing to type into — focusing the first chip or link would pop its tooltip over the header. */
        autoFocus={false}
        title={waitingFor ? (() => {
          const group = laneMap.get(waitingFor.domain)?.group ?? LANE_GROUP_FALLBACK[waitingFor.domain];
          const LaneIcon = GROUP_ICON[group ?? ''] ?? ShoppingCart;
          return (
            <span className="flex min-w-0 items-center gap-3">
              <span className={`grid size-10 shrink-0 place-items-center rounded-card border ${groupStyle(group).chip}`} aria-hidden>
                <LaneIcon size={20} />
              </span>
              <span className="min-w-0">
                <span className="block truncate">{waitingFor.docNo ?? waitingFor.title}</span>
                <span className="mt-1 block">
                  <TypeChip group={group}>
                    {laneMap.get(waitingFor.domain)?.label ?? LANE_LABEL_FALLBACK[waitingFor.domain] ?? waitingFor.domain}
                  </TypeChip>
                </span>
              </span>
            </span>
          );
        })() : ''}
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {waitingFor && (() => {
                const acts = (laneMap.get(waitingFor.domain)?.actions ?? [])
                  .filter((a) => actionApplies(a, waitingFor, meId, myRoles));
                /*
                 * THE ACT THIS DIALOG IS FOR, DRAWN AS THE PRIMARY ONE.
                 *
                 * Only a key literally named `approve` was ever styled primary, so every lane whose
                 * forward act is called something else — Receive, Pick batches, Verify & activate —
                 * put its whole reason for existing in a plain outline button, indistinguishable
                 * from Close beside it. The first NON-danger action is the one the reader came to
                 * take; the rest stay secondary.
                 */
                const lead = acts.find((a) => !a.danger)?.key;
                return acts.map((a) => (
                  <Button
                    key={a.key}
                    variant={a.danger ? 'soft-danger' : a.key === lead ? 'primary' : 'default'}
                    icon={actionIcon(waitingFor.domain, a.key)}
                    onClick={() => {
                      const r = waitingFor;
                      const href = rowHref(r);
                      setWaitingFor(null);
                      // Same two routes as the row's own buttons — see the Decision column above.
                      if (a.openInPlace) {
                        if (href) navigate(withAct(href, a.key));
                        return;
                      }
                      setDecideFor({ item: r, action: a });
                    }}
                  >
                    {a.label}
                  </Button>
                ));
              })()}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="text" onClick={() => setWaitingFor(null)}>Close</Button>
              {waitingFor && rowHref(waitingFor) && (
                <Button
                  variant="default"
                  icon={<ExternalLink size={16} />}
                  onClick={() => { const href = rowHref(waitingFor); setWaitingFor(null); if (href) navigate(href); }}
                >
                  Open the record
                </Button>
              )}
            </div>
          </div>
        }
      >
        {waitingFor && (() => {
          const g = groupStyle(laneMap.get(waitingFor.domain)?.group ?? LANE_GROUP_FALLBACK[waitingFor.domain]);
          const from = waitingFor.meta?.fromCode as string | undefined;
          const to = waitingFor.meta?.toCode as string | undefined;
          const route = !!(from && to);
          /* The route IS the From and To facts, drawn better — so they are not printed a second time in
             the tiles underneath it. */
          const facts = route
            ? waitingFor.details.filter((d) => !(d.chip && (d.chip === from || d.chip === to)))
            : waitingFor.details;
          return (
            <div className="flex flex-col gap-4">
              {/*
                * THE RECORD, IN ITS LANE'S COLOUR — a rail in the lane's hue and its wash fading across.
                * The age rides here too: how long a decision has gone untaken is the reason this page
                * exists, and it was on the row behind the dialog and nowhere inside it.
                */}
              <div className={`relative overflow-hidden rounded-card border border-line/70 bg-linear-to-r ${g.wash} to-transparent py-3.5 pl-5 pr-4 shadow-e1`}>
                <span className={`absolute inset-y-0 left-0 w-1 ${g.bar}`} aria-hidden />
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                  {route ? (
                    /* A floor on the route's width, so on a phone the age pill wraps UNDER it rather than
                       squeezing it — squeezed, the warehouse code (the part that identifies it) truncated. */
                    <div className="flex min-w-[min(16rem,100%)] flex-1 flex-wrap items-center gap-x-3 gap-y-2">
                      <RouteEnd caption="From" code={from!} name={waitingFor.meta?.fromWarehouse} />
                      <span className={`flex min-w-20 flex-1 items-center gap-1.5 ${g.text}`} aria-hidden>
                        <span className="flex-1 border-t-2 border-dashed border-current opacity-40" />
                        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-surface shadow-e1">
                          <Truck size={16} />
                        </span>
                        <span className="flex-1 border-t-2 border-dashed border-current opacity-40" />
                        <ArrowRight size={16} className="shrink-0" />
                      </span>
                      <RouteEnd caption="To" code={to!} name={waitingFor.meta?.toWarehouse} />
                    </div>
                  ) : (
                    <div className="min-w-0 flex-1 type-body-strong text-content">{waitingFor.title}</div>
                  )}
                  <Pill color={ageTone(waitingFor.ageDays)}>
                    <Clock3 size={12} aria-hidden />
                    {waitingFor.ageDays === 0 ? 'waiting today' : `waiting ${plural(waitingFor.ageDays, 'day')}`}
                  </Pill>
                </div>
                {waitingFor.subtitle && (
                  <div className="mt-2 type-caption text-muted">{waitingFor.subtitle}</div>
                )}
              </div>
              <DetailGrid details={facts} maxCols={2} look="dialog" />
            </div>
          );
        })()}
      </Modal>

      {/*
        * ONE DECISION, IN FULL.
        *
        * The table has to fit six lanes into six columns, so it shows the shape of a decision and not
        * the decision itself. Opening a row gives the whole thing in one place — what was decided, on
        * what, by whom, and what sending it back would actually unwind — with a way through to the
        * record it was made on, because that is where the line items and the history live.
        *
        * It is titled with the record's NUMBER, never its row id: "Decision #21" named a database
        * key, which is not what the record is called on any other screen, in any message, or to the
        * person who raised it — and "#21" belongs to a different record in every other lane. Every
        * lane carries a document series now (attendance fixes got `REG/…` last), so the bare word is
        * only the fallback for a row from before its lane had one.
        */}
      <Modal
        open={!!decidedFor}
        onOpenChange={(o) => { if (!o) setDecidedFor(null); }}
        width={620}
        title={decidedFor?.docNo ?? (decidedFor ? 'Decision' : '')}
        description={decidedFor ? (laneMap.get(decidedFor.domain)?.label ?? decidedFor.domain) : ''}
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div>
              {canRevert && decidedFor?.revertTo && (
                <Button
                  variant="soft-danger"
                  icon={<Undo2 size={16} />}
                  onClick={() => { const r = decidedFor; setDecidedFor(null); setRevertFor(r); setRevertReason(''); }}
                >
                  Send back for review
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="text" onClick={() => setDecidedFor(null)}>Close</Button>
              {decidedFor && recordHref(decidedFor) && (
                <Button
                  variant="primary"
                  icon={<ExternalLink size={16} />}
                  onClick={() => { const href = recordHref(decidedFor); setDecidedFor(null); if (href) navigate(href); }}
                >
                  Open the record
                </Button>
              )}
            </div>
          </div>
        }
      >
        {decidedFor && (
          <div className="flex flex-col gap-4">
            {/*
              * TWO FACTS, EACH WITH ITS NAME ON IT.
              *
              * This band used to be a status pill, a bare ₹692 and a chip repeating the subtitle
              * directly above it. The chip said nothing new, and an unlabelled number on a money
              * screen is the one thing a reader has to guess at — claimed? approved? reimbursed?
              *
              * "Value" is what it is called in the column this row came from, and it is the only
              * honest word for it: the figure means something different in every lane (an order's
              * grand total, a return's value, a claim's approved amount), so a more specific label
              * would be right on one lane and wrong on the next.
              */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-card border border-line bg-surface-2/50 px-4 py-3">
                <div className="type-label text-subtle">Outcome</div>
                <div className="mt-1">
                  <Pill color={decidedFor.status === 'REJECTED' ? 'red' : decidedFor.status === 'CLOSED' ? 'purple' : 'green'}>
                    {decidedFor.status}
                  </Pill>
                </div>
              </div>
              <div className="rounded-card border border-line bg-surface-2/50 px-4 py-3">
                <div className="type-label text-subtle">Value</div>
                <div className="mt-1 type-metric tabular-nums text-content">
                  {decidedFor.amount == null
                    ? <span className="type-body text-subtle">not a money decision</span>
                    : inr2(decidedFor.amount)}
                </div>
              </div>
            </div>

            <dl className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
              {[
                /* No "Document" row: the dialog is titled with it. Repeating it costs a line and gives
                   the reader a fact they have already read. */
                {
                  label: 'Who / what',
                  value: decidedFor.party
                    ? (
                      <PartyCell
                        name={decidedFor.party} kind={decidedFor.partyKind} id={decidedFor.partyId}
                        role={decidedFor.partyRole}
                        sub={decidedFor.partySub ? formatIstDate(decidedFor.partySub) : null}
                        className="text-right [&>div:first-child]:justify-end"
                      />
                    )
                    : null,
                },
                /*
                 * THE ROLE IS A BADGE BESIDE THE NAME, not a row of its own.
                 *
                 * It was printed as a second field holding the raw enum lower-cased — "Role ·
                 * accounts" — which reads as a different KIND of fact from the person above it, and
                 * looks nothing like the same role on the row this dialog was opened from. Every
                 * other person in the app wears a coloured pill next to their name; so does this
                 * one now, and the separate row goes (the fact was never two facts).
                 */
                {
                  label: 'Decided by',
                  value: decidedFor.decidedBy
                    ? (
                      <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
                        {decidedFor.decidedBy}
                        <RoleBadge role={decidedFor.decidedByRole} />
                      </span>
                    )
                    : null,
                },
                { label: 'Decided on', value: decidedFor.decidedAt ? formatIst(decidedFor.decidedAt) : null },
                { label: 'How long ago', value: decidedFor.decidedAt ? formatIstRelative(decidedFor.decidedAt) : null },
              ].filter((f) => f.value != null && f.value !== '').map((f) => (
                <div key={f.label} className="flex min-w-0 items-baseline justify-between gap-3 border-b border-line/60 pb-1.5">
                  <dt className="shrink-0 type-label text-subtle">{f.label}</dt>
                  <dd className="min-w-0 break-words text-right type-body text-content">
                    {f.value}
                  </dd>
                </div>
              ))}
            </dl>

            {/*
              * THE EVIDENCE, ON THE SCREEN WHERE THE DECISION IS RE-JUDGED.
              *
              * This dialog exists to answer "was that call right?", and its one action reverses a
              * real ledger credit — yet it showed a name, an amount and a verdict, and the slip that
              * the approval actually rested on was reachable only by leaving for the payments
              * register. The same thumbnails the pending queue shows, on the same record.
              */}
            {!!decidedFor.files?.length && (
              <div className="rounded-card border border-line bg-surface-2/40 px-3.5 py-2.5">
                <div className="mb-1.5 type-overline text-subtle">Proof attached</div>
                <AttachmentCell files={decidedFor.files} label={`${decidedFor.docNo ?? 'Record'} proof`} size={56} maxThumbs={6} />
              </div>
            )}

            {/* What "send back" would cost, stated before the button rather than after it. */}
            {decidedFor.revertTo && (
              <Alert
                type="warning"
                message={`Sending this back returns it to ${decidedFor.revertTo}`}
                description={laneMap.get(decidedFor.domain)?.revert?.effect
                  ?? 'The approval is unwound and the record goes back to its approver’s queue.'}
                icon={<ShieldAlert size={16} />}
              />
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={!!revertFor}
        onOpenChange={(o) => { if (!o) { setRevertFor(null); setRevertReason(''); } }}
        title={`Send ${revertFor?.docNo ?? 'this record'} back for review`}
        footer={
          <>
            <Button variant="default" onClick={() => { setRevertFor(null); setRevertReason(''); }}>Cancel</Button>
            <Button variant="danger" loading={revert.isPending} disabled={revertReason.trim().length < 10}
              onClick={() => revert.mutate()}>
              Send back
            </Button>
          </>
        }
      >
        {revertFor ? (
          <>
            <div className="mb-3 rounded-card border border-line bg-surface-2 p-3">
              <div className="type-label text-subtle">What this will do</div>
              <p className="mt-1 type-body text-content">{laneMap.get(revertFor.domain)?.revert?.effect ?? 'The record returns to its approver.'}</p>
              <p className="mt-2 type-caption text-subtle">
                <Clock size={12} className="mr-1 inline" />
                {revertFor.status} → {revertFor.revertTo}
                {revertFor.decidedBy ? ` · overturning ${revertFor.decidedBy}${revertFor.decidedByRole ? ` (${revertFor.decidedByRole.replace(/_/g, ' ').toLowerCase()})` : ''}` : ''}
              </p>
            </div>
            <label htmlFor="revert-reason" className="mb-1 block type-label text-content">
              Why is this being overturned?<span className="ml-0.5 text-danger-600">*</span>
            </label>
            <Textarea
              id="revert-reason"
              rows={4}
              value={revertReason}
              onChange={(e) => setRevertReason(e.target.value)}
              placeholder="e.g. No credit found in the HDFC statement for this UTR — the money never reached the bank."
            />
            <p className="mt-1 type-caption text-subtle">
              At least 10 characters. This is the permanent record of why the decision was reversed — it is shown to the approver.
            </p>
          </>
        ) : null}
      </Modal>
    </>
  );
}
